
class AjaxWatch {
    static register(listener) {

        this.listeners.push(listener);

        if(!this.patched){
            this._patch();
        }

        // return un-register function
        return () => {
            let index = this.listeners.indexOf(listener);
            if (index !== -1) {
                this.listeners.splice(index, 1);
            }
            if(this.listeners.length === 0 && this.listenersBefore.length === 0){
                this._unpatch();
            }
        }
    }

    static registerBefore(listener) {

        this.listenersBefore.push(listener);

        if(!this.patched){
            this._patch();
        }

        // return un-register function
        return () => {
            let index = this.listenersBefore.indexOf(listener);
            if (index !== -1) {
                this.listenersBefore.splice(index, 1);
            }
            if(this.listeners.length === 0 && this.listenersBefore.length === 0){
                this._unpatch();
            }
        }
    }

    static _callListeners(args){
        for (let i = 0; i < AjaxWatch.listeners.length; i++) {
            const listener = AjaxWatch.listeners[i];
            listener(args);
        }
    }

    static _callBeforeListeners(args){
        let body = args.body;
        for (let i = 0; i < AjaxWatch.listenersBefore.length; i++) {
            const listener = AjaxWatch.listenersBefore[i];
            try {
                const result = listener({ ...args, body });
                if(result !== undefined){
                    body = result;
                }
            } catch (e) {
                // avoid blocking the request on listener errors
                console.error('[ajax-watch] before listener error', e);
            }
        }
        return body;
    }

    static _normalizeUrl(url){
        if(typeof url === 'object'){
            if(url && url.pathname){
                return url.pathname + url.search;
            }
            if(url && url.url){
                return url.url;
            }
        }
        return url;
    }

    static injectCartAddProperties(ajax, payload){
        if(!ajax || !payload){
            return;
        }

        const properties = payload.properties;
        if(!properties || Object.keys(properties).length === 0){
            return;
        }

        const target = AjaxWatch._findCartAddTarget(ajax.body, payload.variantId);
        if(!target){
            return;
        }

        return AjaxWatch._applyPropertiesToBody(ajax.body, properties, target);
    }

    static _isCartAddRequest(ajax){

        const url = AjaxWatch._normalizeUrl(ajax.url);
        const method = ajax.method ? ajax.method.toString().toUpperCase() : 'GET';

        if(!url || !method){
            return false;
        }
        if(method !== 'POST'){
            return false;
        }
        return url.indexOf('/cart/add') !== -1;
    }

    static _findCartAddTarget(body, variantId){
        const hasVariant = typeof variantId !== 'undefined' && variantId !== null;
        const variantKey = hasVariant ? variantId.toString() : null;

        if(body && typeof FormData !== 'undefined' && body instanceof FormData){
            return AjaxWatch._findTargetInParams(body, variantKey);
        }

        if(body && typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams){
            return AjaxWatch._findTargetInParams(body, variantKey);
        }

        if(typeof body === 'string'){
            const trimmed = body.trim();
            if(trimmed.startsWith('{') || trimmed.startsWith('[')){
                try {
                    const parsed = JSON.parse(body);
                    return AjaxWatch._findTargetInObject(parsed, variantKey);
                } catch (e) {
                    // ignore parse error
                }
            }
            if(typeof URLSearchParams !== 'undefined'){
                const params = new URLSearchParams(body);
                return AjaxWatch._findTargetInParams(params, variantKey);
            }
        }

        if(body && typeof body === 'object'){
            return AjaxWatch._findTargetInObject(body, variantKey);
        }

        return null;
    }

    static _findTargetInParams(params, variantKey){
        let id = null;
        let itemIndexes = [];
        let itemMatch = null;

        const entries = typeof params.entries === 'function' ? params.entries() : [];
        for (const entry of entries) {
            const key = entry[0];
            const value = entry[1];
            const match = key.match(/^items\[(\d+)\]\[(id|variant_id)\]$/);
            if(match){
                const index = parseInt(match[1], 10);
                itemIndexes.push(index);
                // target the matching item (upsells can be added in the same request)
                if(variantKey && value && value.toString() === variantKey && (itemMatch === null || index < itemMatch)){
                    itemMatch = index;
                }
            }
        }

        if(itemIndexes.length > 0){
            if(variantKey){
                return itemMatch === null ? null : { type: 'items', index: itemMatch };
            }
            return { type: 'items', index: Math.min.apply(null, itemIndexes) };
        }

        if(typeof params.get === 'function'){
            id = params.get('id') || params.get('variant_id');
        }

        if(!id){
            return null;
        }

        if(variantKey && id.toString() !== variantKey){
            return null;
        }

        return { type: 'single' };
    }

    static _findTargetInObject(body, variantKey){
        if(Array.isArray(body?.items) && body.items.length > 0){
            if(variantKey){
                const index = body.items.findIndex(item => item && ((item.id && item.id.toString() === variantKey) || (item.variant_id && item.variant_id.toString() === variantKey)));
                return index === -1 ? null : { type: 'items', index: index };
            }
            // multiple items -> attach to first item
            return { type: 'items', index: 0 };
        }

        const id = body?.id || body?.variant_id;
        if(!id){
            return null;
        }
        if(variantKey && id.toString() !== variantKey){
            return null;
        }
        return { type: 'single' };
    }

    static _applyPropertiesToBody(body, properties, target){
        if(!body || !target){
            return;
        }

        if(typeof FormData !== 'undefined' && body instanceof FormData){
            return AjaxWatch._applyPropertiesToFormData(body, properties, target);
        }

        if(typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams){
            return AjaxWatch._applyPropertiesToParams(body, properties, target);
        }

        if(typeof body === 'string'){
            const trimmed = body.trim();
            if(trimmed.startsWith('{') || trimmed.startsWith('[')){
                try {
                    const parsed = JSON.parse(body);
                    const updated = AjaxWatch._applyPropertiesToObject(parsed, properties, target);
                    return JSON.stringify(updated);
                } catch (e) {
                    // fall through to query params
                }
            }
            if(typeof URLSearchParams !== 'undefined'){
                const params = new URLSearchParams(body);
                const updatedParams = AjaxWatch._applyPropertiesToParams(params, properties, target);
                return updatedParams.toString();
            }
        }

        if(typeof body === 'object'){
            return AjaxWatch._applyPropertiesToObject(body, properties, target);
        }

        return;
    }


    static _applyPropertiesToFormData(formData, properties, target){
        const prefix = target.type === 'items' ? `items[${target.index}][properties]` : 'properties';
        for (const key in properties) {
            if(!properties.hasOwnProperty(key)){
                continue;
            }
            const field = `${prefix}[${key}]`;
            if(typeof formData.has === 'function' && formData.has(field)){
                continue;
            }
            if(typeof formData.set === 'function'){
                formData.set(field, properties[key]);
            } else {
                formData.append(field, properties[key]);
            }
        }
        return formData;
    }

    static _applyPropertiesToParams(params, properties, target){
        const prefix = target.type === 'items' ? `items[${target.index}][properties]` : 'properties';
        for (const key in properties) {
            if(!properties.hasOwnProperty(key)){
                continue;
            }
            const field = `${prefix}[${key}]`;
            if(params.has(field)){
                continue;
            }
            params.set(field, properties[key]);
        }
        return params;
    }

    static _applyPropertiesToObject(body, properties, target){
        if(target.type === 'single'){
            const existing = body.properties || {};
            body.properties = Object.assign({}, properties, existing);
            return body;
        }

        if(target.type === 'items' && Array.isArray(body.items) && body.items[target.index]){
            const item = body.items[target.index];
            const existing = item.properties || {};
            item.properties = Object.assign({}, properties, existing);
        }

        return body;
    }

    static _patch(){

        AjaxWatch.originalOpen = XMLHttpRequest.prototype.open;
        AjaxWatch.originalSend = XMLHttpRequest.prototype.send;

        // xhr
        XMLHttpRequest.prototype.open = function() {

            // store the used method & url ( data is not passed yet and not passed to listeners )
            let method = arguments[0];
            let url = arguments[1];

            // store on instance for send()
            this.__clAjaxWatch = { method, url };

            this.addEventListener('readystatechange', function() {
                if (this.readyState == 4 && this.status == 200) {
                    // call the listener with the method & url as object key values
                    AjaxWatch._callListeners({ method, url });
                }
            }, !1);

            // call original method
            AjaxWatch.originalOpen.apply(this, arguments);
        };

        XMLHttpRequest.prototype.send = function(body) {
            const meta = this.__clAjaxWatch || {};
            const nextBody = AjaxWatch._callBeforeListeners({
                method: meta.method,
                url: meta.url,
                body,
                xhr: this
            });
            if(nextBody !== undefined){
                body = nextBody;
            }

            return AjaxWatch.originalSend.call(this, body);
        };

        // fetch api
        if('function' == typeof window.fetch){

            AjaxWatch.originalFetch = window.fetch;

            window.fetch = function() {

                let input = arguments[0];
                let init = arguments[1];

                let url = input;
                let method = init !== undefined && init.method ? init.method : (input && input.method ? input.method : 'GET');
                let body = init !== undefined && Object.prototype.hasOwnProperty.call(init, 'body') ? init.body : (input && input.body ? input.body : undefined);

                let nextBody = AjaxWatch._callBeforeListeners({ method, url, body, input, init });
                if(nextBody !== undefined && method && method.toString().toUpperCase() !== 'GET'){
                    // if input is a Request object, create a new Request to update the body
                    if(typeof Request !== 'undefined' && input instanceof Request){
                        const reqInit = {
                            method: method,
                            headers: input.headers,
                            mode: input.mode,
                            credentials: input.credentials,
                            cache: input.cache,
                            redirect: input.redirect,
                            referrer: input.referrer,
                            referrerPolicy: input.referrerPolicy,
                            integrity: input.integrity,
                            keepalive: input.keepalive,
                            signal: input.signal,
                            body: nextBody
                        };
                        input = new Request(input.url, reqInit);
                        init = undefined;
                        url = input;
                    } else {
                        init = init ? { ...init, body: nextBody } : { method, body: nextBody };
                    }
                }

                let promise = AjaxWatch.originalFetch.call(this, input, init);

                promise.then(() => {

                    // NEW: url object (fetch)
                    url = AjaxWatch._normalizeUrl(url);

                    AjaxWatch._callListeners({ method, url });
                });

                // return original promise
                return promise;
            };
        }

        this.patched = true;
    }

    static _unpatch(){
        if(AjaxWatch.originalOpen){
            XMLHttpRequest.prototype.open = AjaxWatch.originalOpen;
        }
        if(AjaxWatch.originalSend){
            XMLHttpRequest.prototype.send = AjaxWatch.originalSend;
        }
        if( AjaxWatch.originalFetch){
            window.fetch =  AjaxWatch.originalFetch;
        }

        this.patched = false;
    }

}

AjaxWatch.patched = false;
AjaxWatch.listeners = [];
AjaxWatch.listenersBefore = [];

export default AjaxWatch;
